import React, { FC } from 'react'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { ActionConfigurationElement } from 'modules/processes/domain'
import { useActionConfigurations } from 'modules/processes/hooks/useActionConfigurations'

import { useFormProvider } from 'modules/processes/components/ItemWizard/Providers/form'
import { useValidationsProvider } from 'modules/processes/components/ItemWizard/Providers/validations'

export interface ActionWizardContextProps {
  actions: ActionConfigurationElement[]
  loading: boolean
  isActionValid: boolean
  configuration: any
  fileIds?: number[]
  updateConfiguration: (configuration: object) => void
  getInitialConfiguration: <T extends object>(state: T) => T
  onFiles: (files: File[]) => void
}

const ActionWizardContext = React.createContext<ActionWizardContextProps | null>(null)

interface ActionWizardProviderProps {
  children: React.ReactNode
}

export const ActionWizardProvider: FC<ActionWizardProviderProps> = ({ children }) => {
  const { actions, loading } = useActionConfigurations()
  const { changeValue, originalItem: item, form } = useFormProvider()
  const [isFormValid, setIsFormValid] = React.useState(false)
  const [isConfigurationValid, setIsConfigurationValid] = React.useState(false)
  const [initialFilesIds, setInitialFilesIds] = React.useState<number[] | undefined>(undefined)
  const { fieldWithErrors } = useValidationsProvider()

  useUnsafeEffect(() => {
    if (item && item?.configuration && form) {
      changeValue('configuration', JSON.parse(String(item?.configuration)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, form])

  useUnsafeEffect(() => {
    setIsFormValid(isConfigurationValid && fieldWithErrors.length === 0)
  }, [isConfigurationValid, fieldWithErrors])

  const updateConfiguration = useUnsafeCallback(
    (configuration: object) => {
      const currentConfiguration = form.get('configuration').value
      const newConfiguration = { ...currentConfiguration, ...configuration }
      changeValue('configuration', newConfiguration)

      validateFormState(configuration, newConfiguration)
    },
    [changeValue, form]
  )

  function validateFormState(configuration: object, newConfiguration: any) {
    const configKeys = Object.keys(configuration)
    const validForm = Object.keys(newConfiguration).every(
      (key) => configKeys.includes(key) && !!newConfiguration[key]
    )

    setIsConfigurationValid(validForm)
  }

  const getInitialConfiguration = useUnsafeCallback(
    (state) => {
      const initialFormConfiguration = form?.get('configuration').value
      const initialItemConfiguration = item?.configuration
        ? JSON.parse(String(item?.configuration))
        : {}
      const isFormEmpty = Object.values(initialFormConfiguration).every((value) => !value)
      const initialConfiguration = isFormEmpty ? initialItemConfiguration : initialFormConfiguration

      const configKeys = Object.keys(initialConfiguration)
      const newState = Object.keys(state).reduce((acc, key) => {
        if (configKeys.includes(key)) {
          return { ...acc, [key]: initialConfiguration[key] }
        }
        return acc
      }, state)

      const newFiles =
        item?.files?.map(
          (file) => new File([file.url], file.filename, { type: file.contentType })
        ) || []

      const actionFileIds = item?.files?.map((file) => file.id) || []

      if (actionFileIds) setInitialFilesIds(actionFileIds)

      validateFormState(initialConfiguration, newState)
      return {
        ...newState,
        files: newFiles,
      }
    },
    [form, item]
  )

  const onFiles = (files: File[]) => {
    form.setValues({
      attachments: files,
    })
  }

  return (
    <ActionWizardContext.Provider
      value={{
        actions,
        loading,
        configuration: form?.get('configuration').value,
        isActionValid: isFormValid,
        getInitialConfiguration,
        updateConfiguration,
        onFiles,
        fileIds: initialFilesIds,
      }}
    >
      {children}
    </ActionWizardContext.Provider>
  )
}
export const useActionWizard = (): ActionWizardContextProps => {
  const ActionWizardModule = React.useContext(ActionWizardContext)

  if (!ActionWizardModule)
    throw new Error('useActionWizard must be used within an ActionWizardProvider')

  return ActionWizardModule
}
